import React, { useRef, FunctionComponent, memo, CSSProperties } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    lightBorderGray,
    altGrayTextColor,
} from "../../../globalStyles/colors/scoutColors";

import { ReportActionButton } from "./Buttons";
import styled from "styled-components";
import { useOutsideClick } from "..";

interface IMenuProps {
  title: string;
  noButton?: boolean;
  mobile?: boolean;
  style?: CSSProperties;
  icon?: JSX.Element;
  iconRight?: JSX.Element;
  type?: string;
  bgColor?: string;
  color?: string;
  disabled?: boolean;
  isOpen: boolean;
  fontSize?: number;
  align?: "left" | "right";
  setIsMenuOpen: (open: boolean) => void;
  buttonStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
  hideTitle?: boolean;
}

interface IMenuLinkProps {
  title?: JSX.Element | string;
  className?: string;
  clickCallback?: () => void;
  style?: CSSProperties;
}

const Wrapper = styled.div`
  position: relative;
`;

const MenuWrapper = styled(motion.div)`
  background-color: #ffffff;
  top: 50px;
  position: absolute;
  box-shadow: 0px 2px 13px 0px rgba(0, 0, 0, 0.1);
  z-index: 200;
  border-radius: 5px;
  min-width: 185px;
  overflow: hidden;
  width: 328px;
  padding: 0;
  > hr {
    border: 1px solid ${lightBorderGray};
    border-top: 0;
  }
`;

const LinkWrapper = styled.a`
  min-width: 120px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
  text-decoration: none;
  color: #00000090;
  cursor: pointer;
  transition: color 0.3s, font-weight 0.3s;
  &:hover,
  &.active {
    background: #f5f6f7;
    color: ${altGrayTextColor};
  }
  &.active {
    font-weight: 500;
  }
`;

export const MenuLink: FunctionComponent<IMenuLinkProps> = memo((props) => {
    return (
        <LinkWrapper
            onClick={props.clickCallback}
            className={props.className}
            style={props.style}
        >
            {props.title}
        </LinkWrapper>
    );
});

export const Menu: FunctionComponent<IMenuProps> = (props) => {
    const ref = useRef();

    useOutsideClick(ref, () => {
        if (props.isOpen) {
            props.setIsMenuOpen(false);
        }
    });

    const menuWrapper = () => {
        let wrapper: JSX.Element = null;
        if (props.isOpen) {
            wrapper = (
                <MenuWrapper
                    key="menu"
                    ref={ref}
                    initial={{ opacity: 0, y: 50, scale: 0.3 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{
                        opacity: 0,
                        scale: 0.5,
                        transition: { duration: 0.2 },
                    }}
                    style={props.align ? { left: "2px" } : { right: "2px" }}
                >
                    {props.children}
                </MenuWrapper>
            );
        }

        return wrapper;
    };

    return (
        <Wrapper style={props.wrapperStyle}>
            <AnimatePresence initial={false}>{menuWrapper()}</AnimatePresence>
            <ReportActionButton
                title={props.title}
                hideTitle={props.hideTitle}
                clickCallback={() => props.setIsMenuOpen(true)}
                menu={true}
                className={props.isOpen ? `active` : ``}
                bgColor={props.bgColor}
                color={props.color}
                icon={props.icon}
                iconRight={props.iconRight}
                disabled={props.disabled}
                fontSize={props.fontSize}
                style={props.buttonStyle}
            />
        </Wrapper>
    );
};
